import { useState, useEffect } from 'react';

export interface ScreenshotErrorEntry {
  createdAt: string;
  error: string;
  screenshotData: any;
}

export interface ScreenshotData {
  screenshotError?: ScreenshotErrorEntry[];
  url?: string;
  screenshotKey?: string;
}

export interface ScreenshotErrorState {
  type: string;
  retryData: ScreenshotData | null | any;
}

export interface UseScreenshotErrorStateProps {
  screenshotData: ScreenshotData;
  isProcessing: boolean;
  retryScreenshotAt?: string | number | Date | null;
  apiDomain: string;
  projectId: string | number;
  taskId: string | number;
}

export const useScreenshotErrorState = ({
  screenshotData,
  isProcessing,
  retryScreenshotAt,
  apiDomain,
  projectId,
  taskId
}: UseScreenshotErrorStateProps): ScreenshotErrorState => {
  const [errorState, setErrorState] = useState<ScreenshotErrorState>({
    type: '',
    retryData: null
  });

  const [needsToFetchData, setNeedsToFetchData] = useState(true);

  const errorStateIsEmpty =
    errorState.type === '' && errorState.retryData === null;

  useEffect(() => {
    const {
      screenshotError: allScreenshotErrors,
      url,
      screenshotKey
    } = screenshotData;

    if (url) {
      if (!errorStateIsEmpty) {
        setErrorState({ type: '', retryData: null });
      }

      return;
    }

    const allScreenshotErrorsWithCreatedAt = allScreenshotErrors?.filter(
      error => error.createdAt
    );

    if (allScreenshotErrorsWithCreatedAt?.length) {
      const sortedErrors = [...allScreenshotErrorsWithCreatedAt].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      const latestError = sortedErrors[sortedErrors.length - 1];
      setErrorState({
        type: latestError.error,
        retryData: latestError.screenshotData
      });
    } else if (!url && !isProcessing && needsToFetchData && screenshotKey) {
      setNeedsToFetchData(false);

      setErrorState({ type: 'generic', retryData: null });

      fetch(
        `${apiDomain}/projects/${projectId}/tasks/${taskId}/screenshot_data_exists?key=${screenshotKey}`
      )
        .then(response => {
          if (response.status === 200) {
            setErrorState(prev => ({ ...prev, retryData: screenshotData }));
          }
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Error checking screenshot data:', error);
          }
        });
    }
  }, [
    isProcessing,
    screenshotData,
    retryScreenshotAt,
    needsToFetchData,
    errorStateIsEmpty
  ]);

  return errorState;
};
